import { readVerificationLinkPurpose } from "./verification-link-purpose.mjs";

const LINK_APPROVAL_KEYS = new Set(["ok", "verified", "purpose", "passwordResetRequired"]);
const CONFIRMATION_KEYS = new Set(["ok", "reset"]);
const NAVIGATION_STATE_KEYS = new Set(["passwordReset"]);
const NAVIGATION_RESET_KEYS = new Set(["resetToken"]);

function isRecord(value) {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

function hasOwn(record, key) {
  return Object.prototype.hasOwnProperty.call(record, key);
}

function hasOnlyKeys(record, allowed) {
  const keys = Object.keys(record);
  return keys.length === allowed.size && keys.every((key) => allowed.has(key));
}

function invalidLinkApproval() {
  const error = new Error("invalid_password_reset_link_response");
  error.code = "invalid_password_reset_link_response";
  return error;
}

function invalidConfirmation() {
  const error = new Error("invalid_password_reset_response");
  error.code = "invalid_password_reset_response";
  return error;
}

/**
 * Accepts only the tokenless approval envelope returned to a password-reset
 * link opener. The original one-time link remains the reset bearer; response
 * fields can never smuggle account or session material into this browser.
 */
export function readPasswordResetLinkApproval(value) {
  if (!isRecord(value)) return null;
  const hasApprovalSignal = value.purpose === "reset" || hasOwn(value, "passwordResetRequired");
  if (!hasApprovalSignal) return null;

  const valid =
    value.ok === true &&
    value.verified === true &&
    value.purpose === "reset" &&
    value.passwordResetRequired === true &&
    hasOnlyKeys(value, LINK_APPROVAL_KEYS);
  if (!valid) throw invalidLinkApproval();
  return value;
}

/**
 * Builds transient React Router state. Reset bearers must never be placed in a
 * query string, path segment, or browser storage.
 */
export function createPasswordResetNavigationState(resetToken) {
  const normalized = String(resetToken || "").trim();
  if (readVerificationLinkPurpose(normalized) !== "reset") {
    throw invalidLinkApproval();
  }
  return { passwordReset: { resetToken: normalized } };
}

/**
 * Fails closed when navigation state was forged, malformed, or contains a
 * verification token issued for another purpose.
 */
export function readPasswordResetNavigationState(value) {
  if (
    !isRecord(value) ||
    !hasOnlyKeys(value, NAVIGATION_STATE_KEYS) ||
    !isRecord(value.passwordReset) ||
    !hasOnlyKeys(value.passwordReset, NAVIGATION_RESET_KEYS)
  ) {
    return null;
  }
  const resetToken =
    typeof value.passwordReset.resetToken === "string" ? value.passwordReset.resetToken.trim() : "";
  return readVerificationLinkPurpose(resetToken) === "reset" ? resetToken : null;
}

export function readPasswordResetConfirmation(value) {
  if (
    !isRecord(value) ||
    value.ok !== true ||
    value.reset !== true ||
    !hasOnlyKeys(value, CONFIRMATION_KEYS)
  ) {
    throw invalidConfirmation();
  }
  return value;
}
